import { useCallback, useEffect, useState } from 'react';
import type { PhotoList, PublicPhoto } from '@karozik/shared';
import { api, Avatar, formatBytes, formatClock, Spinner, useParty } from '@karozik/ui';
import type { AdminEvent } from '../types';

/** The guests' shared photos: kept until the party is deleted, downloadable in one ZIP. */
export function PhotosTab({ event }: { event: AdminEvent }) {
  const conn = useParty({ role: 'admin', eventId: event.id }, { keepReactions: false });
  const [list, setList] = useState<PhotoList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    api<PhotoList>(`/api/admin/events/${event.id}/photos`)
      .then(setList)
      .catch((e: Error) => setError(e.message));
  }, [event.id]);
  // Reload whenever a guest adds or removes a photo.
  useEffect(load, [load, conn.state?.photoCount]);

  const remove = async (p: PublicPhoto) => {
    if (!confirm(`Supprimer cette photo${p.guest ? ` de ${p.guest.pseudo}` : ''} pour tout le monde ?`)) return;
    try {
      await api(`/api/admin/events/${event.id}/photos/${p.id}`, { method: 'DELETE' });
      setList((l) => l && { ...l, photos: l.photos.filter((x) => x.id !== p.id) });
    } catch (e) {
      setError((e as Error).message);
    }
  };

  if (!list) return <div className="flex justify-center py-10">{error ? <p className="text-danger">{error}</p> : <Spinner />}</div>;
  const total = list.photos.reduce((n, p) => n + p.size, 0);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <p className="min-w-0 flex-1 text-sm text-ink-dim">
          {list.photos.length} photos partagées par les invités · {formatBytes(total)}. Visibles par toute personne qui a le lien de la soirée, gardées jusqu’à sa
          suppression.
        </p>
        {list.photos.length > 0 && (
          <a href={list.zipUrl} download className="bg-gradient-neon shrink-0 rounded-xl px-4 py-2 text-sm font-bold">
            ⬇︎ Tout télécharger (.zip)
          </a>
        )}
      </div>
      {error && <p className="mb-3 text-sm text-danger">{error}</p>}
      {list.photos.length === 0 && (
        <p className="rounded-2xl bg-surface px-4 py-8 text-center text-ink-dim">Aucune photo pour l’instant. Les invités en ajoutent depuis leur téléphone.</p>
      )}
      <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {list.photos.map((p) => (
          <li key={p.id} className="overflow-hidden rounded-2xl bg-surface">
            <a href={p.url} target="_blank" rel="noreferrer" className="block aspect-square bg-surface-2">
              <img src={p.thumbUrl} alt={p.guest ? `Photo de ${p.guest.pseudo}` : 'Photo de la soirée'} loading="lazy" className="h-full w-full object-cover" />
            </a>
            <div className="flex items-center gap-1.5 p-1.5">
              <Avatar guest={p.guest} size="sm" />
              <div className="min-w-0 flex-1 text-xs">
                <div className="truncate font-semibold">{p.guest?.pseudo ?? 'Invité'}</div>
                <div className="text-ink-faint">{formatClock(p.createdAt)}</div>
              </div>
              <button onClick={() => void remove(p)} className="rounded-lg bg-surface-2 px-2 py-1 text-xs" aria-label="Supprimer la photo">
                🗑
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
